/**
 * Page du tableau de bord principal
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { appointmentService, servicesAPI } from '../services/api';
import { Appointment } from '../types';
import DashboardStats from '../components/DashboardStats';
import AppointmentList from '../components/AppointmentList';
import SMSTestPanel from '../components/SMSTestPanel';
import socketService from '../services/socketService';
import './Dashboard.css';

/**
 * Composant principal du tableau de bord
 */
const Dashboard: React.FC = () => {
  // États pour les données
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [smsCredits, setSmsCredits] = useState<number>(0);
  
  // États pour le chargement et les erreurs
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Effet pour charger les données au chargement du composant
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupérer les rendez-vous
        const allAppointmentsData = await appointmentService.getAll();
        setAppointments(allAppointmentsData);

        // Récupérer les rendez-vous d'aujourd'hui
        const todayAppointmentsData = await appointmentService.getToday();
        setTodayAppointments(todayAppointmentsData);

        // Récupérer les rendez-vous à venir
        const upcomingAppointmentsData = await appointmentService.getUpcoming();
        setUpcomingAppointments(upcomingAppointmentsData);
        
        // Récupérer le solde de crédits SMS
        const creditsData = await servicesAPI.getSMSCredits();
        setSmsCredits(creditsData.credits || 0);
        
        setLoading(false);
      } catch (err: any) {
        setError('Erreur lors du chargement des données');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, []);
  
  // Effet pour initialiser Socket.IO et écouter les événements
  useEffect(() => {
    // Initialiser la connexion Socket.IO
    const socket = socketService.connect();
    console.log('Socket.IO initialisé dans Dashboard');
    
    // Fonction de callback pour la mise à jour du statut SMS
    const handleSMSStatusUpdate = (data: any) => {
      console.log('Mise à jour du statut SMS reçue dans Dashboard:', data);
      
      // Mettre à jour le statut SMS dans toutes les listes de rendez-vous
      setAppointments(prevAppointments => updateAppointmentSMSStatus(prevAppointments, data));
      setTodayAppointments(prevAppointments => updateAppointmentSMSStatus(prevAppointments, data));
      setUpcomingAppointments(prevAppointments => updateAppointmentSMSStatus(prevAppointments, data));
    };
    
    // Fonction utilitaire pour mettre à jour le statut SMS d'un rendez-vous
    const updateAppointmentSMSStatus = (appointments: Appointment[], data: any) => {
      return appointments.map(appointment => {
        if (appointment.id === data.appointmentId) {
          return {
            ...appointment,
            sms_status: data.status
          };
        }
        return appointment;
      });
    };
    
    // Ajouter l'écouteur d'événement
    socketService.addListener('sms:status-updated', handleSMSStatusUpdate);
    
    // Nettoyer l'écouteur lors du démontage du composant
    return () => {
      socketService.removeListener('sms:status-updated', handleSMSStatusUpdate);
    };
  }, []);

  // Afficher un indicateur de chargement
  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  // Afficher un message d'erreur si nécessaire
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="dashboard">
      {/* En-tête du tableau de bord */}
      <div className="dashboard-header">
        <h1>Tableau de bord</h1>
        <Link to="/appointments/new" className="btn btn-primary">
          Nouveau rendez-vous
        </Link>
      </div>

      {/* Statistiques */}
      <DashboardStats 
        appointments={appointments}
        todayAppointments={todayAppointments}
        upcomingAppointments={upcomingAppointments}
        smsCredits={smsCredits}
      />

      {/* Liste des rendez-vous d'aujourd'hui */}
      <AppointmentList
        title="Rendez-vous d'aujourd'hui"
        appointments={todayAppointments}
        emptyMessage="Aucun rendez-vous aujourd'hui"
        showContactInfo={true}
        showTypeInfo={false}
      />

      {/* Liste des prochains rendez-vous */}
      <AppointmentList
        title="Prochains rendez-vous"
        appointments={upcomingAppointments}
        emptyMessage="Aucun rendez-vous à venir"
        limit={5}
        showContactInfo={false}
        showTypeInfo={true}
        showViewAllLink={true}
        viewAllLink="/appointments"
      />
      
      {/* Panneau de test SMS */}
      <SMSTestPanel />
    </div>
  );
};

export default Dashboard;
